/**
 * Le classeur Excel d'une gamme (registre B22) : un vrai .xlsx mis en forme, pas un fichier de texte.
 *
 * Il part du MÊME document que l'impression (`construireDocumentGamme`) : une variante qui n'autorise pas
 * les prix n'en contient aucun, ni dans une cellule, ni dans une formule, ni dans l'en-tête de page.
 *
 * LOT P2 (registre B26) : la variante « avec prix » porte le prix de VENTE ; la variante interne y ajoute le prix du
 * tarif, le coût d'achat réel et la marge de chaque ligne et de chaque palier (une version sans prix calculés garde
 * la lecture d'avant : prix du tarif, coût d'achat estimé).
 *
 * Une feuille « Récapitulatif » (en-tête de la gamme, un palier par ligne avec son sous-total) puis UNE
 * FEUILLE PAR PALIER, qui s'imprime seule sur une page. Les montants sont de vraies formules — total de
 * ligne = quantité × prix, sous-total = somme des totaux, récapitulatif qui renvoie aux feuilles — pour
 * que Benjamin puisse changer une quantité et voir les totaux suivre (il chiffre aujourd'hui ses contrats
 * à la main). Chaque formule porte aussi son résultat calculé, pour les lecteurs qui ne recalculent pas.
 *
 * `exceljs` est lourd : il n'est chargé qu'au clic sur « Exporter pour Excel » (`chargerExcel`). Les
 * fonctions de ce fichier reçoivent la bibliothèque en paramètre : elles se testent sans navigateur.
 */
import type { Worksheet, Workbook } from 'exceljs';

import {
  type DocumentGamme,
  type LigneDocument,
  type PalierDocument,
  AVERTISSEMENT_INTERNE,
  nomDocument,
  phraseEdition,
  phraseVersion,
} from './gammes-document';
import { EXPLICATION_COUT_ACHAT, reserveAdaptable, reserveMarge, reserveSousTotal } from './gammes-sous-totaux';

export type BibliothequeExcel = typeof import('exceljs');

/** Charge `exceljs` à la demande ; en navigateur la bibliothèque est derrière `default`. */
export async function chargerExcel(): Promise<BibliothequeExcel> {
  const module = (await import('exceljs')) as BibliothequeExcel & { default?: BibliothequeExcel };
  return module.default ?? module;
}

const COULEUR = {
  bleu: 'FF1C4E80',
  bleuClair: 'FFEAF1F8',
  gris: 'FFECEFF3',
  texteGris: 'FF6B7683',
  rouge: 'FFB3261E',
  orange: 'FFB46A00',
  bordure: 'FFBBC3CC',
} as const;

const FORMAT_EURO = '#,##0.00 "€"';
const POLICE = 'Calibri';

type CleColonne =
  | 'designation'
  | 'reference'
  | 'quantite'
  | 'unite'
  | 'tarif'
  | 'prix'
  | 'total'
  | 'refAdaptable'
  | 'prixAdaptable'
  | 'totalAdaptable'
  | 'achat'
  | 'prixAchat'
  | 'totalAchat'
  | 'marge'
  | 'remarque';

interface Colonne {
  readonly cle: CleColonne;
  readonly titre: string;
  readonly largeur: number;
  readonly lettre: string;
  readonly numerique: boolean;
}

/**
 * Les colonnes d'une variante : 4 de base, + 2 de prix (+ le prix du tarif en interne quand le prix principal est celui
 * de vente), + les équivalents, le coût d'achat (réel avec sa marge, ou estimé pour une version sans prix calculés) et
 * la remarque en interne.
 */
function colonnesDe(doc: DocumentGamme): Colonne[] {
  const vente = doc.source === 'VENTE';
  const specs: Array<Omit<Colonne, 'lettre'>> = [
    { cle: 'designation', titre: 'Désignation', largeur: 46, numerique: false },
    { cle: 'reference', titre: 'Référence', largeur: 20, numerique: false },
    { cle: 'quantite', titre: 'Qté', largeur: 8, numerique: true },
    { cle: 'unite', titre: 'Unité', largeur: 8, numerique: false },
  ];
  if (doc.avecPrix) {
    if (doc.interne && vente) specs.push({ cle: 'tarif', titre: 'Prix du tarif HT', largeur: 15, numerique: true });
    specs.push(
      {
        cle: 'prix',
        titre: vente ? (doc.interne ? 'Prix de vente HT' : 'Prix public HT') : 'Prix du tarif HT',
        largeur: 15,
        numerique: true,
      },
      {
        cle: 'total',
        titre: vente ? (doc.interne ? 'Total vente HT' : 'Total HT') : 'Total au tarif HT',
        largeur: 15,
        numerique: true,
      },
    );
  }
  if (doc.interne) {
    specs.push(
      { cle: 'refAdaptable', titre: 'Réf. adaptable', largeur: 20, numerique: false },
      { cle: 'prixAdaptable', titre: 'Prix adaptable HT', largeur: 16, numerique: true },
      { cle: 'totalAdaptable', titre: 'Total adaptable HT', largeur: 16, numerique: true },
    );
    if (doc.avecAchat) {
      specs.push(
        { cle: 'prixAchat', titre: 'Prix d’achat HT', largeur: 15, numerique: true },
        { cle: 'totalAchat', titre: 'Total achat HT', largeur: 15, numerique: true },
      );
      if (vente) specs.push({ cle: 'marge', titre: 'Marge HT', largeur: 15, numerique: true });
    } else {
      specs.push({ cle: 'achat', titre: 'Coût d’achat estimé HT', largeur: 16, numerique: true });
    }
    specs.push({ cle: 'remarque', titre: 'Remarque', largeur: 44, numerique: false });
  }
  return specs.map((s, i) => ({ ...s, lettre: String.fromCharCode(65 + i) }));
}

/** Nom d'onglet valable : 31 caractères au plus, sans `[ ] : * ? / \`, et unique. */
function nomOnglet(base: string, deja: ReadonlySet<string>): string {
  const propre = base.replace(/[[\]:*?/\\]/g, '-').trim().slice(0, 31) || 'Palier';
  let nom = propre;
  for (let i = 2; deja.has(nom.toLowerCase()); i++) nom = `${propre.slice(0, 28)} ${i}`;
  return nom;
}

function citer(nom: string): string {
  return `'${nom.replace(/'/g, "''")}'`;
}

/** Les caractères « & » d'un en-tête ou d'un pied d'impression Excel se doublent. */
function pourEntetePage(texte: string): string {
  return texte.replace(/&/g, '&&');
}

function bordure(feuille: Worksheet, ligne: number, de: number, a: number): void {
  for (let c = de; c <= a; c++) {
    feuille.getCell(ligne, c).border = {
      top: { style: 'thin', color: { argb: COULEUR.bordure } },
      bottom: { style: 'thin', color: { argb: COULEUR.bordure } },
      left: { style: 'thin', color: { argb: COULEUR.bordure } },
      right: { style: 'thin', color: { argb: COULEUR.bordure } },
    };
  }
}

function remplir(feuille: Worksheet, ligne: number, de: number, a: number, argb: string): void {
  for (let c = de; c <= a; c++) {
    feuille.getCell(ligne, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
  }
}

function fusionnee(feuille: Worksheet, ligne: number, de: number, a: number): void {
  if (a > de) feuille.mergeCells(ligne, de, ligne, a);
}

function reglerPage(feuille: Worksheet, doc: DocumentGamme, ligneTitres: number | null): void {
  feuille.pageSetup = {
    paperSize: 9, // A4
    orientation: doc.interne ? 'landscape' : 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
    margins: { left: 0.5, right: 0.5, top: 0.7, bottom: 0.7, header: 0.3, footer: 0.3 },
    ...(ligneTitres ? { printTitlesRow: `${ligneTitres}:${ligneTitres}` } : {}),
  };
  const nom = pourEntetePage(nomDocument(doc));
  feuille.headerFooter.oddFooter = `&L&8${nom}&R&8Page &P / &N`;
  if (doc.interne) {
    feuille.headerFooter.oddHeader = `&C&"Calibri,Bold"&10&KB3261E${pourEntetePage(AVERTISSEMENT_INTERNE)}`;
  }
}

/** Où se trouvent, dans la feuille d'un palier, les cellules dont le récapitulatif a besoin. */
interface CellulesPalier {
  readonly onglet: string;
  readonly total?: string;
  readonly adaptable?: string;
  /** Le coût d'achat du palier : ESTIMÉ (version sans prix calculés) ou RÉEL (somme des coûts d'achat). */
  readonly achat?: string;
  readonly marge?: string;
}

function ecrireLigne(feuille: Worksheet, doc: DocumentGamme, cols: readonly Colonne[], r: number, l: LigneDocument): void {
  const col = (cle: CleColonne) => cols.find((c) => c.cle === cle);
  const lettre = (cle: CleColonne) => col(cle)?.lettre ?? '';
  const numero = (cle: CleColonne) => (col(cle) ? cols.indexOf(col(cle)!) + 1 : 0);

  const designation = l.genre ? `${l.designation} (${l.genre})` : l.designation;
  feuille.getCell(r, numero('designation')).value = designation;
  const reference = feuille.getCell(r, numero('reference'));
  reference.value = l.reference ?? '';
  reference.numFmt = '@'; // une référence est un texte : « 7.4519.00040 » ne doit jamais devenir un nombre
  if (l.quantite !== null) feuille.getCell(r, numero('quantite')).value = l.quantite;
  feuille.getCell(r, numero('unite')).value = l.unite ?? '';

  if (doc.avecPrix) {
    // Le prix du TARIF, en information, seulement en interne quand le prix principal est celui de vente.
    if (col('tarif') && l.prixTarif !== null && l.prixTarif !== undefined) {
      const c = feuille.getCell(r, numero('tarif'));
      c.value = l.prixTarif;
      c.numFmt = FORMAT_EURO;
    }
    if (l.prixPublic !== null && l.prixPublic !== undefined) {
      const c = feuille.getCell(r, numero('prix'));
      c.value = l.prixPublic;
      c.numFmt = FORMAT_EURO;
    }
    if (l.totalCentimes !== null && l.totalCentimes !== undefined) {
      const c = feuille.getCell(r, numero('total'));
      c.value = { formula: `ROUND(${lettre('quantite')}${r}*${lettre('prix')}${r},2)`, result: l.totalCentimes / 100 };
      c.numFmt = FORMAT_EURO;
    }
  }

  if (doc.interne) {
    feuille.getCell(r, numero('refAdaptable')).value = l.referenceAdaptable ?? '';
    feuille.getCell(r, numero('refAdaptable')).numFmt = '@';
    if (l.prixAdaptable !== null && l.prixAdaptable !== undefined) {
      const c = feuille.getCell(r, numero('prixAdaptable'));
      c.value = l.prixAdaptable;
      c.numFmt = FORMAT_EURO;
    }
    if (l.totalAdaptableCentimes !== null && l.totalAdaptableCentimes !== undefined) {
      const c = feuille.getCell(r, numero('totalAdaptable'));
      c.value = {
        formula: `ROUND(${lettre('quantite')}${r}*${lettre('prixAdaptable')}${r},2)`,
        result: l.totalAdaptableCentimes / 100,
      };
      c.numFmt = FORMAT_EURO;
    }

    if (doc.avecAchat) {
      // Le coût d'achat RÉEL (celui de l'équivalent quand il existe) : prix, total de la ligne, marge de la ligne.
      if (l.prixAchat !== null && l.prixAchat !== undefined) {
        const c = feuille.getCell(r, numero('prixAchat'));
        c.value = l.prixAchat;
        c.numFmt = FORMAT_EURO;
      }
      if (l.totalAchatCentimes !== null && l.totalAchatCentimes !== undefined) {
        const c = feuille.getCell(r, numero('totalAchat'));
        c.value = { formula: `ROUND(${lettre('quantite')}${r}*${lettre('prixAchat')}${r},2)`, result: l.totalAchatCentimes / 100 };
        c.numFmt = FORMAT_EURO;
      }
      // La marge d'une ligne n'existe que si elle a à la fois un total de vente et un total d'achat : la somme des
      // marges d'un palier est donc celle des SEULES lignes comparables, comme dans le document.
      if (col('marge')) {
        const c = feuille.getCell(r, numero('marge'));
        const comparable =
          l.totalCentimes !== null && l.totalCentimes !== undefined && l.totalAchatCentimes !== null && l.totalAchatCentimes !== undefined;
        const t = `${lettre('total')}${r}`;
        const a = `${lettre('totalAchat')}${r}`;
        c.value = {
          formula: `IF(AND(ISNUMBER(${t}),ISNUMBER(${a})),${t}-${a},"")`,
          result: comparable ? ((l.totalCentimes as number) - (l.totalAchatCentimes as number)) / 100 : '',
        };
        c.numFmt = FORMAT_EURO;
      }
    } else {
      // Version sans prix calculés : le coût d'achat ESTIMÉ — l'équivalent quand il existe, sinon la pièce d'origine
      // au prix du tarif.
      const achat = l.totalAdaptableCentimes ?? l.totalCentimes ?? null;
      if (achat !== null) {
        const c = feuille.getCell(r, numero('achat'));
        c.value = {
          formula: `IF(ISNUMBER(${lettre('totalAdaptable')}${r}),${lettre('totalAdaptable')}${r},${lettre('total')}${r})`,
          result: achat / 100,
        };
        c.numFmt = FORMAT_EURO;
      }
    }
    if (l.note) feuille.getCell(r, numero('remarque')).value = l.note;
  }

  for (const c of cols) {
    const cellule = feuille.getCell(r, cols.indexOf(c) + 1);
    cellule.alignment = {
      vertical: 'top',
      wrapText: c.cle === 'designation' || c.cle === 'remarque',
      ...(c.numerique ? { horizontal: 'right' as const } : {}),
    };
    cellule.font = {
      name: c.cle === 'reference' || c.cle === 'refAdaptable' ? 'Consolas' : POLICE,
      size: c.cle === 'remarque' ? 9 : 10,
      ...(c.cle === 'remarque' ? { color: { argb: COULEUR.texteGris } } : {}),
    };
  }
  bordure(feuille, r, 1, cols.length);
}

function feuillePalier(
  classeur: Workbook,
  doc: DocumentGamme,
  palier: PalierDocument,
  nom: string,
  cols: readonly Colonne[],
): CellulesPalier {
  const feuille = classeur.addWorksheet(nom, { views: [{ state: 'frozen', ySplit: 5, showGridLines: false }] });
  const n = cols.length;
  cols.forEach((c, i) => {
    feuille.getColumn(i + 1).width = c.largeur;
  });

  // Ligne 1 : le palier. Ligne 2 : sa cadence. Ligne 3 : la gamme. Ligne 4 : l'avertissement interne.
  feuille.getCell(1, 1).value = `Palier ${palier.code} — ${palier.libelle}`;
  feuille.getCell(1, 1).font = { name: POLICE, size: 14, bold: true, color: { argb: COULEUR.bleu } };
  remplir(feuille, 1, 1, n, COULEUR.bleuClair);
  fusionnee(feuille, 1, 1, n);
  feuille.getRow(1).height = 24;
  feuille.getCell(2, 1).value = palier.cadence;
  feuille.getCell(2, 1).font = { name: POLICE, size: 10, italic: true, color: { argb: COULEUR.texteGris } };
  fusionnee(feuille, 2, 1, n);
  feuille.getCell(3, 1).value = `${doc.titre} — ${phraseVersion(doc.version)} — ${phraseEdition(doc)}`;
  feuille.getCell(3, 1).font = { name: POLICE, size: 9, color: { argb: COULEUR.texteGris } };
  fusionnee(feuille, 3, 1, n);
  if (doc.interne) {
    feuille.getCell(4, 1).value = AVERTISSEMENT_INTERNE;
    feuille.getCell(4, 1).font = { name: POLICE, size: 10, bold: true, color: { argb: COULEUR.rouge } };
    feuille.getCell(4, 1).alignment = { horizontal: 'center' };
    fusionnee(feuille, 4, 1, n);
  }

  // Ligne 5 : les titres de colonnes.
  cols.forEach((c, i) => {
    const cellule = feuille.getCell(5, i + 1);
    cellule.value = c.titre;
    cellule.font = { name: POLICE, size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    cellule.alignment = { vertical: 'middle', wrapText: true, horizontal: c.numerique ? 'right' : 'left' };
  });
  remplir(feuille, 5, 1, n, COULEUR.bleu);
  bordure(feuille, 5, 1, n);
  feuille.getRow(5).height = 30;

  // Les lignes.
  let r = 6;
  const premiere = r;
  if (palier.lignes.length === 0) {
    feuille.getCell(r, 1).value = 'Aucune ligne pour ce palier.';
    feuille.getCell(r, 1).font = { name: POLICE, size: 10, italic: true, color: { argb: COULEUR.texteGris } };
    fusionnee(feuille, r, 1, n);
    r += 1;
  } else {
    for (const l of palier.lignes) {
      ecrireLigne(feuille, doc, cols, r, l);
      r += 1;
    }
  }
  const derniere = r - 1;

  // Sous-totaux.
  const lettre = (cle: CleColonne) => cols.find((c) => c.cle === cle)?.lettre ?? '';
  const numero = (cle: CleColonne) => cols.findIndex((c) => c.cle === cle) + 1;
  let total: string | undefined;
  let adaptable: string | undefined;
  let achat: string | undefined;
  let marge: string | undefined;

  if (palier.sousTotal && palier.lignes.length > 0) {
    const ligneTotal = r;
    const prixDe =
      doc.source === 'VENTE' ? (doc.interne ? ' au prix de vente' : '') : ' au prix du tarif';
    feuille.getCell(ligneTotal, 1).value = `Sous-total HT${prixDe} — palier ${palier.code}`;
    fusionnee(feuille, ligneTotal, 1, numero('prix'));
    const cTotal = feuille.getCell(ligneTotal, numero('total'));
    cTotal.value = {
      formula: `SUM(${lettre('total')}${premiere}:${lettre('total')}${derniere})`,
      result: palier.sousTotal.centimes / 100,
    };
    total = `${lettre('total')}${ligneTotal}`;
    if (palier.sousTotalAdaptable) {
      const cAd = feuille.getCell(ligneTotal, numero('totalAdaptable'));
      cAd.value = {
        formula: `SUM(${lettre('totalAdaptable')}${premiere}:${lettre('totalAdaptable')}${derniere})`,
        result: palier.sousTotalAdaptable.adaptableCentimes / 100,
      };
      cAd.numFmt = FORMAT_EURO;
      adaptable = `${lettre('totalAdaptable')}${ligneTotal}`;
      // Sans coût d'achat réel : le coût ESTIMÉ du palier (équivalent sinon prix du tarif).
      if (!doc.avecAchat) {
        const cAchat = feuille.getCell(ligneTotal, numero('achat'));
        cAchat.value = {
          formula: `SUM(${lettre('achat')}${premiere}:${lettre('achat')}${derniere})`,
          result: palier.sousTotalAdaptable.achatCentimes / 100,
        };
        cAchat.numFmt = FORMAT_EURO;
        achat = `${lettre('achat')}${ligneTotal}`;
      }
    }
    // Avec coût d'achat réel : la somme des coûts d'achat du palier, et la somme des marges des lignes comparables.
    if (doc.avecAchat && palier.sousTotalAchat) {
      const cAchat = feuille.getCell(ligneTotal, numero('totalAchat'));
      cAchat.value = {
        formula: `SUM(${lettre('totalAchat')}${premiere}:${lettre('totalAchat')}${derniere})`,
        result: palier.sousTotalAchat.centimes / 100,
      };
      cAchat.numFmt = FORMAT_EURO;
      achat = `${lettre('totalAchat')}${ligneTotal}`;
    }
    if (doc.avecAchat && palier.marge && numero('marge') > 0) {
      const cMarge = feuille.getCell(ligneTotal, numero('marge'));
      cMarge.value = {
        formula: `SUM(${lettre('marge')}${premiere}:${lettre('marge')}${derniere})`,
        result: palier.marge.margeCentimes / 100,
      };
      cMarge.numFmt = FORMAT_EURO;
      marge = `${lettre('marge')}${ligneTotal}`;
    }
    cTotal.numFmt = FORMAT_EURO;
    for (let c = 1; c <= n; c++) {
      const cellule = feuille.getCell(ligneTotal, c);
      const incomplet =
        (c === numero('total') && !palier.sousTotal.complet) ||
        (c === numero('totalAchat') && palier.sousTotalAchat !== undefined && !palier.sousTotalAchat.complet) ||
        (c === numero('marge') && palier.marge !== undefined && !palier.marge.complete);
      cellule.font = {
        name: POLICE,
        size: 10,
        bold: true,
        ...(incomplet ? { color: { argb: COULEUR.orange } } : {}),
      };
      cellule.alignment = { horizontal: c === 1 ? 'left' : 'right', vertical: 'middle' };
    }
    remplir(feuille, ligneTotal, 1, n, COULEUR.gris);
    bordure(feuille, ligneTotal, 1, n);
    r += 1;

    const reserves = [
      reserveSousTotal(palier.sousTotal),
      doc.interne && !doc.avecAchat && palier.sousTotalAdaptable ? reserveAdaptable(palier.sousTotalAdaptable) : null,
      palier.marge ? reserveMarge(palier.marge) : null,
    ].filter((t): t is string => t !== null);
    for (const texte of reserves) {
      feuille.getCell(r, 1).value = texte;
      feuille.getCell(r, 1).font = { name: POLICE, size: 9, italic: true, color: { argb: COULEUR.orange } };
      feuille.getCell(r, 1).alignment = { wrapText: true, vertical: 'top' };
      fusionnee(feuille, r, 1, n);
      r += 1;
    }
  }


  reglerPage(feuille, doc, 5);
  return {
    onglet: nom,
    ...(total ? { total } : {}),
    ...(adaptable ? { adaptable } : {}),
    ...(achat ? { achat } : {}),
    ...(marge ? { marge } : {}),
  };
}

function remplirRecapitulatif(feuille: Worksheet, doc: DocumentGamme, cellules: readonly CellulesPalier[]): void {
  const vente = doc.source === 'VENTE';
  const reel = doc.interne && doc.avecAchat;
  const avecMarge = reel && vente;
  // Palier | Cadence | Lignes | [sous-total] | [sous-total adaptable | coût d'achat (estimé ou réel) | marge].
  const titres = ['Palier', 'Cadence', 'Lignes'];
  if (doc.avecPrix) {
    titres.push(vente ? (doc.interne ? 'Sous-total au prix de vente HT' : 'Sous-total HT') : 'Sous-total au prix du tarif HT');
  }
  if (doc.interne) titres.push('Sous-total adaptable HT', reel ? 'Coût d’achat réel HT' : 'Coût d’achat estimé HT');
  if (avecMarge) titres.push('Marge HT');
  const colonnes = titres.length;
  [34, 36, 10, 18, 20, 20, 18].slice(0, colonnes).forEach((l, i) => {
    feuille.getColumn(i + 1).width = l;
  });
  let r = 1;

  if (doc.entreprise) {
    feuille.getCell(r, 1).value = doc.entreprise.toUpperCase();
    feuille.getCell(r, 1).font = { name: POLICE, size: 10, bold: true, color: { argb: COULEUR.bleu } };
    r += 1;
  }
  feuille.getCell(r, 1).value = 'Gamme d’entretien';
  feuille.getCell(r, 1).font = { name: POLICE, size: 20, bold: true };
  feuille.getRow(r).height = 28;
  r += 1;
  feuille.getCell(r, 1).value = doc.titre;
  feuille.getCell(r, 1).font = { name: POLICE, size: 13, bold: true, color: { argb: COULEUR.texteGris } };
  r += 2;

  if (doc.interne) {
    feuille.getCell(r, 1).value = AVERTISSEMENT_INTERNE;
    feuille.getCell(r, 1).font = { name: POLICE, size: 11, bold: true, color: { argb: COULEUR.rouge } };
    feuille.getCell(r, 1).alignment = { horizontal: 'center' };
    fusionnee(feuille, r, 1, colonnes);
    const trait = { style: 'medium' as const, color: { argb: COULEUR.rouge } };
    for (let c = 1; c <= colonnes; c++) {
      feuille.getCell(r, c).border = {
        top: trait,
        bottom: trait,
        ...(c === 1 ? { left: trait } : {}),
        ...(c === colonnes ? { right: trait } : {}),
      };
    }
    r += 2;
  }

  const meta: Array<[string, string]> = [['Marque', doc.marque]];
  if (doc.modeles.length > 0) meta.push(['Modèles', doc.modeles.join(' · ')]);
  if (doc.references.length > 0) meta.push(['Réf. constructeur', doc.references.join(' ou ')]);
  meta.push(['Version', phraseVersion(doc.version)], ['Édition', phraseEdition(doc)]);
  if (doc.machine) {
    const m = doc.machine;
    const machine = [m.designation, m.modele, m.referenceConstructeur ? `réf. ${m.referenceConstructeur}` : null, m.numeroSerie ? `n° ${m.numeroSerie}` : null]
      .filter(Boolean)
      .join(' — ');
    meta.push(['Machine', machine]);
    const lieu = [m.client, m.site].filter(Boolean).join(' — ');
    if (lieu) meta.push(['Client / site', lieu]);
  }
  for (const [libelle, valeur] of meta) {
    feuille.getCell(r, 1).value = libelle;
    feuille.getCell(r, 1).font = { name: POLICE, size: 10, color: { argb: COULEUR.texteGris } };
    feuille.getCell(r, 2).value = valeur;
    feuille.getCell(r, 2).font = { name: POLICE, size: 10 };
    fusionnee(feuille, r, 2, colonnes);
    r += 1;
  }
  r += 1;

  // Le tableau : un palier par ligne.
  titres.forEach((t, i) => {
    const cellule = feuille.getCell(r, i + 1);
    cellule.value = t;
    cellule.font = { name: POLICE, size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    cellule.alignment = { vertical: 'middle', wrapText: true, horizontal: i >= 2 ? 'right' : 'left' };
  });
  remplir(feuille, r, 1, colonnes, COULEUR.bleu);
  bordure(feuille, r, 1, colonnes);
  feuille.getRow(r).height = 30;
  r += 1;

  doc.paliers.forEach((palier, i) => {
    const cible = cellules[i];
    feuille.getCell(r, 1).value = `Palier ${palier.code} — ${palier.libelle}`;
    feuille.getCell(r, 2).value = palier.cadence;
    feuille.getCell(r, 3).value = palier.lignes.length;
    if (doc.avecPrix && palier.sousTotal && cible?.total) {
      const c = feuille.getCell(r, 4);
      c.value = { formula: `${citer(cible.onglet)}!${cible.total}`, result: palier.sousTotal.centimes / 100 };
      c.numFmt = FORMAT_EURO;
    }
    if (doc.interne && palier.sousTotalAdaptable && cible?.adaptable) {
      const a = feuille.getCell(r, 5);
      a.value = { formula: `${citer(cible.onglet)}!${cible.adaptable}`, result: palier.sousTotalAdaptable.adaptableCentimes / 100 };
      a.numFmt = FORMAT_EURO;
    }
    if (doc.interne && cible?.achat) {
      // Le coût d'achat du palier : RÉEL (somme des coûts d'achat) ou ESTIMÉ (équivalent sinon prix du tarif).
      const centimes = reel ? palier.sousTotalAchat?.centimes : palier.sousTotalAdaptable?.achatCentimes;
      if (centimes !== undefined) {
        const b = feuille.getCell(r, 6);
        b.value = { formula: `${citer(cible.onglet)}!${cible.achat}`, result: centimes / 100 };
        b.numFmt = FORMAT_EURO;
      }
    }
    if (avecMarge && palier.marge && cible?.marge) {
      const m = feuille.getCell(r, 7);
      m.value = { formula: `${citer(cible.onglet)}!${cible.marge}`, result: palier.marge.margeCentimes / 100 };
      m.numFmt = FORMAT_EURO;
    }
    for (let c = 1; c <= colonnes; c++) {
      const cellule = feuille.getCell(r, c);
      cellule.alignment = { vertical: 'top', wrapText: c <= 2, horizontal: c >= 3 ? 'right' : 'left' };
      // Les montants en gras ; celui d'un palier incomplet en orange : il ne doit pas paraître juste.
      const incomplet =
        (c === 4 && palier.sousTotal !== undefined && !palier.sousTotal.complet) ||
        (c === 6 && reel && palier.sousTotalAchat !== undefined && !palier.sousTotalAchat.complet) ||
        (c === 7 && palier.marge !== undefined && !palier.marge.complete);
      cellule.font = {
        name: POLICE,
        size: 10,
        ...(c >= 4 ? { bold: true } : {}),
        ...(incomplet ? { color: { argb: COULEUR.orange } } : {}),
      };
    }
    bordure(feuille, r, 1, colonnes);
    r += 1;
  });

  // Ce que les chiffres ne disent pas tout seuls.
  const notes: string[] = [];
  for (const p of doc.paliers) {
    const reserve = p.sousTotal ? reserveSousTotal(p.sousTotal) : null;
    if (reserve) notes.push(`Palier ${p.code} — ${reserve}`);
    const reserveDeMarge = p.marge ? reserveMarge(p.marge) : null;
    if (reserveDeMarge) notes.push(`Palier ${p.code} — ${reserveDeMarge}`);
  }
  if (doc.interne && doc.recapitulatifAdaptable) notes.push(EXPLICATION_COUT_ACHAT);
  if (notes.length > 0) r += 1;
  for (const texte of notes) {
    feuille.getCell(r, 1).value = texte;
    feuille.getCell(r, 1).font = { name: POLICE, size: 9, italic: true, color: { argb: COULEUR.orange } };
    feuille.getCell(r, 1).alignment = { wrapText: true, vertical: 'top' };
    fusionnee(feuille, r, 1, colonnes);
    r += 1;
  }

  reglerPage(feuille, doc, null);
}

/** Construit le classeur d'un document. Pur : ne touche ni au disque ni au navigateur. */
export function construireClasseur(excel: BibliothequeExcel, doc: DocumentGamme): Workbook {
  const classeur = new excel.Workbook();
  classeur.creator = doc.entreprise ?? 'Air Indu SaaS';
  classeur.title = nomDocument(doc);
  classeur.subject = `Gamme d’entretien ${doc.code}`;
  classeur.created = new Date(`${doc.edition}T12:00:00`);

  // Le récapitulatif ouvre le classeur : on crée sa feuille en premier, et on la remplit une fois les
  // paliers écrits, puisqu'il renvoie à leurs cellules.
  const recapitulatif = classeur.addWorksheet('Récapitulatif', { views: [{ showGridLines: false }] });
  const cols = colonnesDe(doc);
  const pris = new Set<string>(['récapitulatif']);
  const cellules: CellulesPalier[] = [];
  for (const palier of doc.paliers) {
    const nom = nomOnglet(`Palier ${palier.code}`, pris);
    pris.add(nom.toLowerCase());
    cellules.push(feuillePalier(classeur, doc, palier, nom, cols));
  }
  remplirRecapitulatif(recapitulatif, doc, cellules);
  return classeur;
}

/** Les octets du fichier .xlsx. */
export async function octetsClasseur(classeur: Workbook): Promise<Uint8Array> {
  const tampon = await classeur.xlsx.writeBuffer();
  return new Uint8Array(tampon as ArrayBuffer);
}
